import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress, // 1. Import adicionado
} from '@mui/material';
import { Header } from 'components/home';
import { ContainerForm } from 'components/shared/container_form';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import { useNavigate, useParams } from 'react-router-dom';
import { CuffaAPI } from 'api/cuffa/cuffa.api'; // 1. Import da API adicionado
import { ROUTES } from 'routes/constants';

interface CreditData {
  institution: string;
  creditLine: string;
  creditType: string;
  amount: string;
  year: string;
}

interface CreditViewData {
  accessedPronaf: string;
  records: CreditData[];
}

export const formatCentsToCurrency = (cents: number): string => {
  if (typeof cents !== 'number' || isNaN(cents)) return '';
  const value = cents / 100;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
};

export const CreditoFinanciamentoVisualizar: React.FC = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();

  // 2. Estado de 'loading' adicionado e 'useCuffaStore' removido
  const [dados, setDados] = useState<CreditViewData | null>(null);
  const [loading, setLoading] = useState(true);

  // 3. useEffect reescrito para buscar dados da API e mapeá-los
  useEffect(() => {
    const fetchCreditData = async () => {
      if (!uuid) {
        setLoading(false);
        return;
      }
      // Primeiro tente restaurar do localStorage (específico por uuid, depois global)
      try {
        const storageKeys: string[] = [];
        if (uuid) storageKeys.push(`cuffa-storage-${uuid}`);
        storageKeys.push('cuffa-storage');

        for (const storageKey of storageKeys) {
          try {
            const raw = typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null;
            if (!raw) continue;
            const parsed = JSON.parse(raw);
            const saved = parsed?.state?.dadosParciais?.creditoFinanciamento;
            if (!saved) continue;

            // Pode ser o payload no formato da API ou um formato front-end salvo previamente
            let accessedPronaf = '';
            let records: CreditData[] = [];

            if (typeof saved.accessedPronaf === 'string') {
              accessedPronaf = saved.accessedPronaf;
            } else if (typeof saved.accessed_credit_outside_pronaf === 'boolean') {
              accessedPronaf = saved.accessed_credit_outside_pronaf ? 'sim' : 'nao';
            }

            if (Array.isArray(saved.records)) {
              records = saved.records.map((r: any) => ({
                institution: r.institution || r.financial_institution || '',
                creditLine: r.creditLine || r.credit_line || '',
                creditType: r.creditType || r.credit_type || '',
                amount: (typeof r.amount === 'string' && r.amount) || (typeof r.contracted_value === 'number' ? formatCentsToCurrency(r.contracted_value) : ''),
                year: r.year || r.contraction_year || ''
              }));
            } else if (Array.isArray(saved.credit_contracts)) {
              records = saved.credit_contracts.map((c: any) => ({
                institution: c.financial_institution || '',
                creditLine: c.credit_line || '',
                creditType: c.credit_type || '',
                amount: typeof c.contracted_value === 'number' ? formatCentsToCurrency(c.contracted_value) : (c.contracted_value || ''),
                year: c.contraction_year || ''
              }));
            }

            setDados({ accessedPronaf: accessedPronaf || 'nao', records });
            setLoading(false);
            return;
          } catch (e) {
            console.warn(`Falha ao parsear ${storageKey} ao restaurar creditoFinanciamento:`, e);
            continue;
          }
        }
      } catch (e) {
        console.warn('Erro ao acessar localStorage cuffa-storage ao restaurar creditoFinanciamento', e);
      }

      // Se não encontrou no storage, buscar pela API
      try {
        const response = await CuffaAPI.getDataByID(uuid);

        if (response && response.dados) {
          const apiData = response.dados;

          const mappedData: CreditViewData = {
            accessedPronaf: apiData.accessed_credit_outside_pronaf ? 'sim' : 'nao',
            records: (apiData.credit_contracts || []).map((contract: any) => ({
              institution: contract.financial_institution,
              creditLine: contract.credit_line,
              creditType: contract.credit_type,
              amount: formatCentsToCurrency(contract.contracted_value),
              year: contract.contraction_year || ''
            }))
          };
          setDados(mappedData);
        } else {
          console.error("Dados de crédito não encontrados na resposta da API:", response);
          setDados(null);
        }
      } catch (error) {
        console.error("Erro ao buscar dados de crédito:", error);
        setDados(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCreditData();
  }, [uuid]);

  // 4. Adicionados blocos para exibir o status de 'Carregando' e 'Erro'
  if (loading) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Crédito e Financiamento" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      </MenuProvider>
    );
  }

  if (!dados) {
    return (
      <MenuProvider>
        <TopNavBar />
        <Header title="Crédito e Financiamento" showBackButton />
        <Box width="100%" display="flex" justifyContent="center" margin="2rem 0">
          <Typography>Não foi possível carregar os dados. Verifique o console para mais detalhes.</Typography>
        </Box>
      </MenuProvider>
    );
  }

  // O restante do código JSX continua o mesmo
  return (
    <MenuProvider>
      <TopNavBar />
      <Header title="Visualização do Crédito e Financiamento" showBackButton />
      <Box width="100%" display="flex" justifyContent="center" margin="2rem">
        <Box sx={{ maxWidth: '1152px', padding: '2rem', width: '100%' }}>
          <ContainerForm title="Crédito e Financiamento" width="100%">
            <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Acessou crédito fora do PRONAF?
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Chip
                  label={dados.accessedPronaf === 'sim' ? 'Sim' : 'Não'}
                  color={dados.accessedPronaf === 'sim' ? 'primary' : 'default'}
                  variant="outlined"
                />
              </Box>
            </Paper>

            {dados.accessedPronaf === 'sim' && (
              <Paper elevation={0} sx={{ p: 3, mb: 4, border: '2px solid #000', borderRadius: '8px' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Registros de Crédito Rural
                </Typography>

                {dados.records.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Instituição Financeira</strong></TableCell>
                          <TableCell><strong>Linha de Crédito</strong></TableCell>
                          <TableCell><strong>Tipo de Crédito</strong></TableCell>
                          <TableCell><strong>Valor Contratado</strong></TableCell>
                          <TableCell><strong>Ano</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dados.records.map((record, index) => (
                          <TableRow key={index}>
                            <TableCell>{record.institution}</TableCell>
                            <TableCell>{record.creditLine}</TableCell>
                            <TableCell>{record.creditType}</TableCell>
                            <TableCell>{record.amount}</TableCell>
                            <TableCell>{record.year}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum registro de crédito adicionado
                  </Typography>
                )}
              </Paper>
            )}

            <Box marginTop="2rem" display="flex" justifyContent="center" gap="1rem">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => navigate(ROUTES.CANAIS_COMERCIALIZACAO_CUFFA_VISUALIZAR.replace(':uuid', uuid ?? ''))}
              >
                Voltar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (uuid) {
                    navigate(ROUTES.FONTES_RENDA_FAMILIA_CUFFA_VISUALIZAR.replace(':uuid', uuid));
                  }
                }}
              >
                Avançar
              </Button>
            </Box>
          </ContainerForm>
        </Box>
      </Box>
    </MenuProvider>
  );
};

export default CreditoFinanciamentoVisualizar;
